import rootStore from "@/app/store";
import crud, { ApiResponse } from "../crud";

export interface Invitation {
  id: number;
  business_profile_id: number;
  type: "existing_renter" | "new_renter";
  opaque_id: string;
  agreement_id: number;
  meta_id: number | null;
  is_active: boolean;
  created_at: string;
  price: number;
  start_date: string;
  end_date: string;
  renter_name: string;
}

export interface InvitationMeta {
  renter_name: string;
  address: string;
  ogrn: string;
  inn: number;
  kpp: string;
  bik: string;
  bank_account: string;
  correspondent_account: string;
  bank_name: string;
  phone_number: string;
  email: string;
}

export interface InvitationMetaRequisite {
  requisite_name: string;
  address: string;
  ogrn: string;
  inn: number;
  kpp: string;
  bik: string;
  bank_account: string;
  correspondent_account: string;
  bank_name: string;
  phone_number: string;
  email: string;
}

export interface InvitationCreateExisting {
  business_profile_id: number;
  start_date: string;
  end_date: string;
  meetingHours: number;
  offices: Array<{ office_id: number; price: number }>;
  workplaces: Array<{ workplace_id: number; price: number }>;
  renter: number;
  requisite: number;
  type: "existing_renter";
  number: string;
  meta?: InvitationMetaRequisite;
}

export interface InvitationCreateNew {
  business_profile_id: number;
  start_date: string;
  end_date: string;
  meetingHours: number;
  offices: Array<{ office_id: number; price: number }>;
  workplaces: Array<{ workplace_id: number; price: number }>;
  requisite: number;
  type: "new_renter";
  number: string;
  meta: InvitationMeta;
}

export interface InvitationUpdate {
  business_profile_id: number;
  start_date: string;
  end_date: string;
  meetingHours: number;
  offices: Array<{ office_id: number; price: number }>;
  workplaces: Array<{ workplace_id: number; price: number }>;
  renter?: number;
  requisite: number;
  type: "existing_renter" | "new_renter";
  number: string;
  meta?: InvitationMeta | InvitationMetaRequisite;
}

export type InvitationCreate = InvitationCreateExisting | InvitationCreateNew;

export type InvitationMetaResponse = InvitationMeta | InvitationMetaRequisite;

export function InvitationsAPI(baseUrl: string) {
  return {
    list: async () => {
      const businessProfileId =
        rootStore.getState().businessProfile.businessProfile.id;
      return (await crud.get(
        `${baseUrl}/invitations?business_profile_id=${businessProfileId}`,
        undefined,
        true,
      )) as ApiResponse<Invitation[]>;
    },
    get: async (id: number) => {
      const businessProfileId =
        rootStore.getState().businessProfile.businessProfile.id;
      return (await crud.get(
        `${baseUrl}/invitations/${id}?business_profile_id=${businessProfileId}`,
        undefined,
        true,
      )) as ApiResponse<Invitation>;
    },
    create: async (data: InvitationCreate) => {
      const businessProfileId =
        rootStore.getState().businessProfile.businessProfile.id;
      return (await crud.post(
        `${baseUrl}/invitations?business_profile_id=${businessProfileId}`,
        data,
        true,
      )) as ApiResponse<Invitation>;
    },
    update: async (id: number, data: InvitationUpdate) => {
      const businessProfileId =
        rootStore.getState().businessProfile.businessProfile.id;
      return (await crud.put(
        `${baseUrl}/invitations/${id}?business_profile_id=${businessProfileId}`,
        data,
        true,
      )) as ApiResponse<{}>;
    },
    delete: async (id: number) => {
      const businessProfileId =
        rootStore.getState().businessProfile.businessProfile.id;
      return (await crud.delete(
        `${baseUrl}/invitations/${id}?business_profile_id=${businessProfileId}`,
        true,
      )) as ApiResponse<{}>;
    },
    getMeta: async (metaId: number) => {
      const businessProfileId =
        rootStore.getState().businessProfile.businessProfile.id;
      return (await crud.get(
        `${baseUrl}/invitations/meta/${metaId}?business_profile_id=${businessProfileId}`,
        undefined,
        true,
      )) as ApiResponse<InvitationMetaResponse>;
    },
  };
}
